import dayjs from 'dayjs'
import http, { endpoints } from '../http'
import { mapCaseDetailsRequestParamsToDto, mapCaseFiltersFromDto, mapDatahubCaseFromDto } from './helpers'
import { IDatahubCase, IGetCasesListParams, IUpdateCaseAddressParams, ICaseDetailsRequestParams } from './models'
import { ICaseFiltersDto, IDatahubCaseDto } from './dtos'
import { LocalizedError } from 'localization'
import { IBranch } from '../users/models'
import startCase from 'lodash/startCase'
import { IContactPersonDto } from '../users/dtos'
import { mapContactPersonFromDto } from '../users/helpers'
import { ICommonResponse } from '../types'
import { CASES_SEARCH_V3_ENDPOINT } from 'constants/server/apiEndPoint'

const datahubClient = http.createClient(`${endpoints.datahub}`)

export default class CasesApi {
  static async getDatahubCase(id: IDatahubCase['id'], options?: { signal?: AbortSignal }) {
    const response = await datahubClient.get<IDatahubCaseDto>(`/Cases/${id}`, {
      signal: options?.signal,
    })
    return response?.data ? mapDatahubCaseFromDto(response.data) : undefined
  }

  static async getDatahubCases({
    page = 0,
    pageSize = 100,
    departmentId,
    fromDate,
    toDate,
    query,
    category,
    orderBy,
    orderByDirection,
    // orderByDirection = 'descend',
    includeNoOrders,
    status,
    signal
  }: IGetCasesListParams) {
    const response = await datahubClient.post<{
      total: number
      results: IDatahubCaseDto[]
    }>(CASES_SEARCH_V3_ENDPOINT, {
      // skip: page * pageSize,
      // take: pageSize,
      branchIds: departmentId,
      modifiedDateFrom: fromDate && dayjs(fromDate).format('YYYY-MM-DD'),
      modifiedDateTo: toDate && dayjs(toDate).format('YYYY-MM-DD'),
      deliveredProductCategories: category,
      orderStatuses: status ? status.map(value => startCase(value.toLowerCase())) : undefined,
      searchKey: query,
      sortOrder: orderBy,
      sortDirection: orderByDirection,
      // sortDirection: orderByDirection && orderByDirection === 'descend' ? 'desc' : 'asc',
      mustHaveOrder: !includeNoOrders,
    }, {
      signal
    })

    const { data } = response || {}

    return {
      page: page!,
      pageSize: pageSize!,
      totalItems: data?.total || 0,
      items: data?.results.map(mapDatahubCaseFromDto) || [],
    }
  }

  static async getFilters() {
    const response = await datahubClient.get<ICaseFiltersDto>('/Cases/Search/Filters')
    return response?.data && mapCaseFiltersFromDto(response.data)
  }

  static async getAddressByCaseId(caseId: IDatahubCase['id']) {
    const { addressObj } = (await this.getDatahubCase(caseId)) || {}
    return addressObj
  }

  static async updateCaseAddress({ caseId, ...params }: IUpdateCaseAddressParams) {
    const response = await datahubClient.post<ICommonResponse>(`/cases/${caseId}/address`, params)
    return response?.data
  }

  static async checkIfCaseExistsByCaseId(caseId: IDatahubCase['id']) {
    const foundCase = await this.getDatahubCase(caseId)
    return !!foundCase
  }

  static async checkIfCaseExists(
    referenceOrId: IDatahubCase['reference'] | IDatahubCase['id'],
    branchId: IBranch['id']
  ) {
    const filters = await this.getFilters()

    const { items } = await this.getDatahubCases({
      pageSize: 1,
      departmentId: [branchId],
      query: referenceOrId,
      category: filters.categories,
      status: filters.statuses,
      includeNoOrders: true,
    })

    return !!items?.[0]
  }

  static async upsertCase(params: ICaseDetailsRequestParams) {
    const response = await datahubClient.post<{ caseId: IDatahubCase['id'] }>(
      '/Cases/upsert/webshop',
      mapCaseDetailsRequestParamsToDto(params)
    )

    if (!response?.data?.caseId) {
      throw new LocalizedError(
        params.case.id ? 'cases.messages.caseDetailsUpdateFailed' : 'cases.messages.caseCreateFailed'
      )
    }

    return response.data.caseId
  }

  static async getContactPersonsByCaseId(caseId: IDatahubCase['id']) {
    const response = await datahubClient.get<IContactPersonDto[]>(`/cases/${caseId}/contact-persons`)

    return response?.data ? response.data.map(mapContactPersonFromDto) : []
  }
}
